import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  FileSearch,
  MessageSquare,
  BarChart3,
  Workflow,
  Bot,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  PrimaryButton,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const aiFeatures = [
  {
    code: "A-01",
    icon: FileSearch,
    title: "Resume parsing",
    description: "Advanced LLM extraction of skills, experience, education and qualifications from any format.",
    benefits: ["95%+ accuracy", "Multi-language", "Instant parsing"],
    accent: C.coral,
  },
  {
    code: "A-02",
    icon: Brain,
    title: "Predictive fit scoring",
    description: "ML ranks candidates against role and historical hiring data with explainable signals.",
    benefits: ["Bias reduction", "Pattern matching", "Audit-ready"],
    accent: C.mint,
  },
  {
    code: "A-03",
    icon: MessageSquare,
    title: "JD generation",
    description: "Auto-generate SEO-optimised, inclusive, ATS-friendly job descriptions in seconds.",
    benefits: ["Industry templates", "Inclusive tone", "ATS-friendly"],
    accent: C.gold,
  },
  {
    code: "A-04",
    icon: Bot,
    title: "Candidate chatbot",
    description: "24/7 conversational AI handles queries, schedules interviews and sends updates.",
    benefits: ["Always online", "Multi-language", "Natural tone"],
    accent: C.coral,
  },
  {
    code: "A-05",
    icon: BarChart3,
    title: "Predictive analytics",
    description: "Forecast hiring needs, time-to-fill and cost-per-hire with explainable models.",
    benefits: ["Trend analysis", "ROI optimisation", "Resource planning"],
    accent: C.mint,
  },
  {
    code: "A-06",
    icon: Workflow,
    title: "Workflow automation",
    description: "Plug into Zapier, Make and UiPath. Automate every step of recruitment.",
    benefits: ["1000+ integrations", "Custom flows", "Zero-code"],
    accent: C.gold,
  },
];

const automationFlows = [
  { idx: "01", title: "Auto-screen & rank",  steps: ["Resume submitted", "AI parses & scores", "Top candidates flagged", "Recruiter notified"] },
  { idx: "02", title: "Smart scheduling",     steps: ["Candidate approved", "AI finds slots",     "Calendar invites sent",  "Reminders automated"] },
  { idx: "03", title: "Feedback loop",        steps: ["Interview completed", "Feedback collected", "AI analyses patterns",   "Models improve"] },
];

export default function AIAutomation() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 border-b ds-bg-spotlight" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>AI · 06 specialised agents</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5.25rem] xl:text-[6rem] font-extrabold leading-[0.92] tracking-tight max-w-5xl"
              style={{ color: C.forest }}
            >
              AI that does
              <br />
              <span style={{ color: `${C.forest}80` }}>the boring part.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-3xl"
              style={{ color: C.grid }}
            >
              Six specialised agents handle the heavy lifting parsing, scoring,
              drafting, scheduling, analysing so your team focuses on people,
              not paperwork.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* AGENT GRID */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>The agent stack</StatusBadge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                Six agents, one orchestrator.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>06 agents · 24/7 uptime</TechLabel>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {aiFeatures.map((f, i) => (
              <motion.div
                key={f.code}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="ds-spatial relative p-7 md:p-8 transition-colors duration-200 hover:bg-white"
                style={{ background: C.paper, borderLeft: `4px solid ${f.accent}` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 flex items-center justify-center" style={{ background: C.forest, color: C.paper }}>
                    <f.icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <TechLabel style={{ color: f.accent }}>{f.code}</TechLabel>
                </div>
                <h3 className="text-xl font-bold leading-tight" style={{ color: C.forest }}>{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>{f.description}</p>
                <ul className="mt-4 space-y-1.5">
                  {f.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-xs" style={{ color: C.grid }}>
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: f.accent }} strokeWidth={2.2} />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* AUTOMATION FLOWS */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline, background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge color={C.mint}>Real flows · live</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              What automation actually looks like.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px" style={{ background: "rgba(158,255,191,0.22)" }}>
            {automationFlows.map((flow) => (
              <div key={flow.idx} className="p-7 md:p-8" style={{ background: C.forest }}>
                <TechLabel style={{ color: C.mint }}>flow · {flow.idx}</TechLabel>
                <h3 className="mt-2 text-xl font-bold leading-tight">{flow.title}</h3>
                <ul className="mt-5 space-y-3">
                  {flow.steps.map((step, i) => (
                    <li key={step} className="flex items-start gap-3">
                      <span
                        className="w-6 h-6 flex items-center justify-center text-[10px] font-bold border flex-shrink-0 mt-0.5"
                        style={{ borderColor: C.mint, color: C.mint, letterSpacing: "0.05em" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm leading-snug" style={{ color: "rgba(247,247,245,0.85)" }}>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <PageContainer>
          <div className="relative border p-10 md:p-14 text-center" style={{ borderColor: C.hairline, background: C.paper }}>
            <CornerMarkers color={C.forest} />
            <div className="flex justify-center mb-5">
              <StatusBadge>See it on your data</StatusBadge>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              Bring a real role.
              <br />
              <span style={{ color: `${C.forest}80` }}>See real ranking.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: C.grid }}>
              We'll set up a sandbox in 24 hours with a representative sample of your candidates.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <PrimaryButton>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Book a sandbox
                </PrimaryButton>
              </Link>
              <Link to={createPageUrl("Products")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#1A3C2B]/[0.04] transition-colors duration-150"
                  style={{ borderColor: C.forest, color: C.forest }}
                >
                  Tour the products
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
